import React, { useEffect, useRef, useState } from "react";

import { format, isValid, parse, startOfDay } from "date-fns";
import { enUS, fr } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import { Controller, get, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import ErrorMessage from "./ErrorMessage";
import FieldContainer from "./FieldContainer";
import Label from "./Label";
import { Button } from "../ui/button";
import { Calendar } from "../ui/calendar";
import { Input } from "../ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

import type { Locale } from "date-fns";
import type { JSX } from "react";
import type { Matcher } from "react-day-picker";

function getDisplayFormat(language: string): string {
  return language === "fr" ? "dd/MM/yyyy" : "MM/dd/yyyy";
}

function isDateWithinBounds(
  date: Date,
  minDate?: Date,
  maxDate?: Date,
): boolean {
  const day = startOfDay(date).getTime();

  if (minDate && day < startOfDay(minDate).getTime()) return false;
  if (maxDate && day > startOfDay(maxDate).getTime()) return false;

  return true;
}

interface DatePickerControlProps {
  name: string;
  value?: Date;
  displayFormat: string;
  locale: Locale;
  minDate?: Date;
  maxDate?: Date;
  disabledDays: Matcher[];
  isInvalid?: boolean;
  isReadOnly?: boolean;
  open: boolean;
  setOpen: (open: boolean) => void;
  container?: HTMLElement | null;
  inputRef: React.RefObject<HTMLInputElement | null>;
  onChange: (date: Date | null) => void;
  onBlur: () => void;
}

function DatePickerControl({
  name,
  value,
  displayFormat,
  locale,
  minDate,
  maxDate,
  disabledDays,
  isInvalid,
  isReadOnly,
  open,
  setOpen,
  container,
  inputRef,
  onChange,
  onBlur,
}: DatePickerControlProps): JSX.Element {
  const [month, setMonth] = useState<Date | undefined>(value);
  const [inputValue, setInputValue] = useState<string>(
    value ? format(value, displayFormat, { locale }) : "",
  );

  // Resync the displayed text with the controlled value, unless the user is
  // currently typing in the field (so we never reformat mid-edit).
  useEffect(() => {
    if (document.activeElement === inputRef.current) return;

    setInputValue(value ? format(value, displayFormat, { locale }) : "");
    setMonth(value);
  }, [value, displayFormat, locale, inputRef]);

  const handleInputChange = (rawValue: string): void => {
    setInputValue(rawValue);

    if (!rawValue.trim()) {
      onChange(null);
      return;
    }

    const parsedDate = parse(rawValue, displayFormat, new Date(), { locale });

    if (
      !isValid(parsedDate) ||
      !isDateWithinBounds(parsedDate, minDate, maxDate)
    )
      return;

    setMonth(parsedDate);
    onChange(parsedDate);
  };

  const handleInputBlur = (): void => {
    setInputValue(value ? format(value, displayFormat, { locale }) : "");
    setMonth(value);
    onBlur();
  };

  return (
    <div className="relative w-full">
      <Input
        ref={inputRef}
        aria-invalid={isInvalid}
        className="pr-9"
        disabled={isReadOnly}
        id={name}
        onBlur={handleInputBlur}
        placeholder={displayFormat.toUpperCase()}
        value={inputValue}
        onChange={(event): void => {
          handleInputChange(event.target.value);
        }}
        onClick={(): void => {
          if (!isReadOnly) setOpen(true);
        }}
        onKeyDown={(event): void => {
          if (event.key === "ArrowDown") {
            event.preventDefault();
            setOpen(true);
          }
        }}
      />
      <Popover onOpenChange={setOpen} open={open}>
        <PopoverTrigger asChild>
          <Button
            className="absolute top-1/2 right-1 size-7 -translate-y-1/2"
            disabled={isReadOnly}
            type="button"
            variant="ghost"
          >
            <CalendarIcon className="size-3.5" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          className="z-[1600] w-auto overflow-hidden p-0"
          container={container}
          onOpenAutoFocus={(event): void => event.preventDefault()}
        >
          <Calendar
            disabled={disabledDays}
            endMonth={maxDate}
            locale={locale}
            mode="single"
            month={month}
            onMonthChange={setMonth}
            selected={value}
            startMonth={minDate}
            onSelect={(selectedDate): void => {
              if (!selectedDate) return;

              onChange(selectedDate);
              setOpen(false);
            }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

interface DatePickerProps {
  name: string;
  label?: string;
  sublabel?: string;
  isRequired?: boolean;
  isCompact?: boolean;
  className?: string;
  isReadOnly?: boolean;
  minDate?: Date;
  maxDate?: Date;
  portalId?: string;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  onChange?: (date: Date | null) => void;
}

function DatePicker({
  name,
  label,
  sublabel,
  isRequired,
  isCompact,
  className,
  isReadOnly,
  minDate,
  maxDate,
  portalId,
  isOpen,
  onOpenChange,
  onChange,
}: DatePickerProps): JSX.Element {
  const {
    control,
    formState: { errors },
    getFieldState,
  } = useFormContext();
  const { t, i18n } = useTranslation();

  const [internalOpen, setInternalOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const error = get(errors, name);
  const isInvalid = getFieldState(name)?.invalid;

  const locale: Locale = i18n.language === "fr" ? fr : enUS;
  const displayFormat = getDisplayFormat(i18n.language);

  const open = isOpen ?? internalOpen;
  const setOpen = onOpenChange ?? setInternalOpen;

  const disabledDays = [
    minDate ? { before: minDate } : undefined,
    maxDate ? { after: maxDate } : undefined,
  ].filter(Boolean) as Matcher[];

  const container = portalId ? document.getElementById(portalId) : undefined;

  return (
    <FieldContainer className={className} isCompact={isCompact}>
      {label ? (
        <Label
          isCompact={isCompact}
          isRequired={isRequired}
          label={label}
          name={name}
          sublabel={sublabel}
        />
      ) : null}
      <div className="relative w-full">
        <Controller
          control={control}
          name={name}
          render={({ field }): JSX.Element => {
            const value: Date | undefined =
              field.value instanceof Date ? field.value : undefined;

            return (
              <DatePickerControl
                container={container}
                disabledDays={disabledDays}
                displayFormat={displayFormat}
                inputRef={inputRef}
                isInvalid={isInvalid}
                isReadOnly={isReadOnly}
                locale={locale}
                maxDate={maxDate}
                minDate={minDate}
                name={name}
                onBlur={field.onBlur}
                open={open}
                setOpen={setOpen}
                value={value}
                onChange={(date): void => {
                  onChange?.(date);
                  field.onChange(date);
                }}
              />
            );
          }}
          rules={{
            required:
              isRequired &&
              t("requiredField", {
                ns: "editor-errors",
              }),
          }}
        />
        <ErrorMessage error={error} />
      </div>
    </FieldContainer>
  );
}

export default DatePicker;
